<template>
  <div class="min-h-screen bg-gray-50">
    <!-- ヘッダー -->
    <header class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <router-link to="/dashboard" class="text-2xl font-bold text-gray-900">
              Soarimetric
            </router-link>
          </div>
          <nav class="flex items-center space-x-4">
            <router-link to="/dashboard" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              ダッシュボード
            </router-link>
          </nav>
        </div>
      </div>
    </header>

    <main class="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        <h1 class="text-3xl font-bold text-gray-900 mb-8">エリア管理</h1>

        <!-- 登録済みエリア -->
        <div class="bg-white shadow rounded-lg mb-8">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-lg font-medium text-gray-900 mb-4">
              登録済みエリア ({{ userAreas.length }}/3)
            </h2>
            
            <div v-if="!userAreas.length" class="text-center py-8">
              <div class="text-4xl mb-4">📍</div>
              <p class="text-gray-500">まだエリアが登録されていません</p>
            </div>

            <div v-else class="space-y-4">
              <div 
                v-for="area in userAreas" 
                :key="area.id"
                class="p-4 border border-gray-200 rounded-lg"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <h3 class="text-lg font-medium text-gray-900">{{ area.area_name }}</h3>
                    <p class="text-sm text-gray-500 mb-2">{{ area.area_name_eng }}</p>
                    
                    <!-- 新しいフィールド表示 -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div v-if="getAreaDetails(area)?.takeoff_elevation">
                        <span class="font-medium text-gray-700">離陸高度:</span>
                        <span class="ml-1 text-gray-600">{{ getAreaDetails(area).takeoff_elevation }}m</span>
                      </div>
                      <div v-if="getAreaDetails(area)?.landing_elevation">
                        <span class="font-medium text-gray-700">着陸高度:</span>
                        <span class="ml-1 text-gray-600">{{ getAreaDetails(area).landing_elevation }}m</span>
                      </div>
                      <div v-if="getAreaDetails(area)?.sport_types" class="sm:col-span-2">
                        <span class="font-medium text-gray-700">対応スポーツ:</span>
                        <span class="ml-1 text-gray-600">{{ getAreaDetails(area).sport_types }}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    @click="removeArea(area.id)"
                    class="text-red-600 hover:text-red-800 font-medium ml-4 flex-shrink-0"
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- エリア追加 -->
        <div v-if="userAreas.length < 3" class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-lg font-medium text-gray-900 mb-4">エリアを追加</h2>
            
            <div class="space-y-4">
              <div>
                <label for="area-select" class="block text-sm font-medium text-gray-700">
                  エリアを選択
                </label>
                <select
                  v-model="selectedAreaId"
                  id="area-select"
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">エリアを選択してください</option>
                  <option 
                    v-for="area in availableAreas" 
                    :key="area.id" 
                    :value="area.id"
                  >
                    {{ area.area_name }} ({{ area.region }}) - {{ area.sport_types || 'スポーツ情報なし' }}
                  </option>
                </select>
              </div>

              <div v-if="errorMessage" class="text-red-600 text-sm">
                {{ errorMessage }}
              </div>

              <button
                @click="addArea"
                :disabled="!selectedAreaId || isLoading"
                class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {{ isLoading ? '追加中...' : 'エリアを追加' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p class="text-yellow-800">
            最大3つのエリアまで登録できます。新しいエリアを追加するには、既存のエリアを削除してください。
          </p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useWeatherStore } from '@/stores/weather'

export default {
  name: 'Areas',
  setup() {
    const weatherStore = useWeatherStore()
    
    const areas = ref([])
    const userAreas = ref([])
    const selectedAreaId = ref('')
    const errorMessage = ref('')
    const isLoading = ref(false)

    const availableAreas = computed(() => {
      if (!areas.value || !Array.isArray(areas.value)) return []
      if (!userAreas.value || !Array.isArray(userAreas.value)) return areas.value
      
      const userAreaIds = userAreas.value.map(area => area.area_name_eng)
      return areas.value.filter(area => !userAreaIds.includes(area.area_name_eng))
    })

    const loadData = async () => {
      try {
        await Promise.all([
          loadAreas(),
          loadUserAreas()
        ])
      } catch (error) {
        console.error('Data loading failed:', error)
      }
    }

    const loadAreas = async () => {
      try {
        const data = await weatherStore.fetchAreas()
        areas.value = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Areas fetch failed:', error)
      }
    }

    const loadUserAreas = async () => {
      try {
        const data = await weatherStore.fetchUserAreas()
        userAreas.value = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('User areas fetch failed:', error)
      }
    }

    const addArea = async () => {
      if (!selectedAreaId.value || isLoading.value) return
      
      errorMessage.value = ''
      isLoading.value = true

      try {
        const selectedArea = areas.value.find(area => area.id === selectedAreaId.value)
        
        const areaData = {
          flight_area_id: selectedArea.id
        }

        const newArea = await weatherStore.addUserArea(areaData)
        userAreas.value.push(newArea)
        selectedAreaId.value = ''
      } catch (error) {
        
        if (error.response?.status === 400) {
          // バックエンドからのバリデーションエラーメッセージを使用
          const errorData = error.response.data
          if (typeof errorData === 'object' && errorData.non_field_errors) {
            errorMessage.value = errorData.non_field_errors[0]
          } else if (typeof errorData === 'string') {
            errorMessage.value = errorData
          } else {
            errorMessage.value = '最大3つのエリアまで登録できます'
          }
        } else {
          errorMessage.value = 'エリアの追加に失敗しました'
        }
      } finally {
        isLoading.value = false
      }
    }

    const removeArea = async (areaId) => {
      if (!confirm('このエリアを削除しますか？')) return

      try {
        await weatherStore.removeUserArea(areaId)
        userAreas.value = userAreas.value.filter(area => area.id !== areaId)
      } catch (error) {
        console.error('Area removal failed:', error)
        alert('エリアの削除に失敗しました')
      }
    }

    const getAreaDetails = (userArea) => {
      // userAreaからarea_name_engを使ってareasから詳細情報を取得
      if (!areas.value || !Array.isArray(areas.value)) return null
      return areas.value.find(area => area.area_name_eng === userArea.area_name_eng)
    }

    onMounted(() => {
      loadData()
    })

    return {
      areas,
      userAreas,
      availableAreas,
      selectedAreaId,
      errorMessage,
      isLoading,
      addArea,
      removeArea,
      getAreaDetails
    }
  }
}
</script>